import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

/** A person node in the Web of Wyrd. Mirrors berserkr.fetch_case_web. */
export interface WebNode {
  key: string;
  label: string | null;
  kind: string | null;       // party | counsel | neutral | third_party | regulator | unknown
  represents: string | null; // plaintiff | defendant | medicap | widow | estate | ...
}
export interface CommEdge {
  source: string; // node key
  target: string; // node key
  weight: number; // # shared threads
}
export interface CharEdge {
  source: string; // claimant full name
  target: string; // target full name (or 'third_party')
  type: string;   // claim_type
  defendant_relevant: boolean;
  weight: number;
}
export interface CaseWeb {
  nodes: WebNode[];
  comm_edges: CommEdge[];
  char_edges: CharEdge[];
}

/**
 * The Web of Wyrd — who is tied to whom. Communication ties (co-participation
 * on threads) and accusation threads (characterizations claimant -> target).
 */
export function useCaseWeb(caseId: string | undefined) {
  return useQuery<CaseWeb, Error>({
    queryKey: ["case-web", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      if (!caseId) return { nodes: [], comm_edges: [], char_edges: [] };
      const { data, error } = await berserkr().rpc("fetch_case_web", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as CaseWeb) ?? { nodes: [], comm_edges: [], char_edges: [] };
    },
  });
}
